import React, { useState } from "react";
import { Button, Card, Col, Container, Image, Row, Spinner, Stack } from "react-bootstrap";
import { fetchUsers, fetchCMUsers, deleteUser, fetchUserCases } from "../../services/httpClient";
import { User } from "../../types/user";
import useApp from "../../utils/useApp";
import PageTitle from "../ui/PageTitle";
import CaseReassignmentModal from "./CaseReassignmentModal";
import IconButton from "../ui/IconButton";
import { InfoCircleOutline } from "../ui/InfoCircleOutline";

interface UserTabState {
  users: User[];
  description?: string;
  totalCount?: number;
  setUsers: any;
  setCases: any;
  type: string;
  displayType: string;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
  onEditUser: (id: string | number) => void;
}

const UserTab = ({
  users,
  description,
  totalCount,
  setUsers,
  setCases,
  type,
  displayType,
  setShowToast,
  setToastText,
  setToastBody,
  onEditUser,
}: UserTabState) => {
  const { auth }: any = useApp();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [userToDelete, setUserToDelete] = useState<User>();
  const [showCaseReassignmentModal, setShowCaseReassignmentModal] = useState<boolean>(false);
  const [casesToReassign, setCasesToReassign] = useState<any[]>([]);
  const [cmUsers, setCmUsers] = useState<any[]>([]);

  const editUser = (e: any) => {
    onEditUser(e);
  };

  const onClickDelete = (e: any) => {
    setIsDeleting(true);
    setUserToDelete(e);
  };

  const onClickDeleteFinal = () => {
    deleteUser(userToDelete?.id)
      .then(() => {
        if (userToDelete?.cases && userToDelete.cases.length > 0) {
          // show reassignment modal
          setIsDeleting(false);
          setCasesToReassign(userToDelete.cases);
          setShowCaseReassignmentModal(true);

          fetchCMUsers()
            .then((res: any) => {
              const users = res[0].data
                .filter((u: any) => u.id !== userToDelete.id) // Exclude the deleted user
                .map((i: any) => {
                  return {
                    id: i.id,
                    key: i.id,
                    name: i.firstname.length === 0 ? i.email : `${i.firstname} ${i.lastname ?? ""}`,
                  };
                });
              setCmUsers(users);
            })
            .catch((err: any) => {
              console.log(err);
              setIsLoading(false);
            });
        } else {
          // No cases to reassign, complete deletion immediately
          setIsLoading(true);
          completeUserDeletion();
        }
      })
      .catch((err: any) => {
        console.log(err);
        setIsLoading(false);
      });
  };

  const completeUserDeletion = async () => {
    setShowToast(true);
    setToastText("User Deleted");
    setToastBody(userToDelete?.firstname + " " + (userToDelete?.lastname ?? ""));
    setUserToDelete(undefined);
    setIsDeleting(false);

    await getCases();

    fetchUsers()
      .then((res: any) => {
        const userList = res[0].data;
        setUsers(userList);
        setIsLoading(false);
      })
      .catch((err: any) => {
        console.log(err);
        setIsLoading(false);
      });
  };

  const getCases = async () => {
    await fetchUserCases()
      .then((res: any) => {
        var c = res[0].data;
        setCases(c);
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  const handleCaseReassignmentComplete = () => {
    setShowCaseReassignmentModal(false);
    setIsLoading(true);
    completeUserDeletion();
  };

  const getCaseTooltip = (user: any) => {
    const tooltipParts = [];

    if (user.waitlistCases > 0) {
      tooltipParts.push(`${user.waitlistCases} Waitlist Case${user.waitlistCases > 1 ? "s" : ""}`);
    }

    if (user.inactiveCases > 0) {
      tooltipParts.push(`${user.inactiveCases} Inactive Case${user.inactiveCases > 1 ? "s" : ""}`);
    }

    return tooltipParts.join(", ");
  };

  const onClickCancelDelete = () => {
    setIsDeleting(false);
    setUserToDelete(undefined);
  };

  const renderDelete = () => {
    return (
      <Card className="bg-light">
        <Card.Body>
          <Stack direction="horizontal" gap={3}>
            <Stack style={{ justifyContent: "center" }}>
              <Card.Subtitle>
                Are you sure you want to delete {userToDelete?.firstname + " " + (userToDelete?.lastname ?? "")}?
              </Card.Subtitle>
            </Stack>
            <Button className="app-danger-color button" onClick={() => onClickDeleteFinal()}>
              Confirm Delete
            </Button>
            <div className="vr" />
            <Button className="app-primary-color button" onClick={() => onClickCancelDelete()}>
              Cancel
            </Button>
          </Stack>
        </Card.Body>
      </Card>
    );
  };

  const renderCannotDeleteThemself = () => {
    return (
      <Card className="bg-light">
        <Card.Body>
          <Stack direction="horizontal" gap={3}>
            <Stack style={{ justifyContent: "center" }}>
              <Card.Subtitle>You cannot delete your own {displayType} account.</Card.Subtitle>
            </Stack>
            <Button className="app-primary-color button" onClick={() => onClickCancelDelete()}>
              Cancel
            </Button>
          </Stack>
        </Card.Body>
      </Card>
    );
  };

  const renderCannotDeleteAll = () => {
    return (
      <Card className="bg-light">
        <Card.Body>
          <Stack direction="horizontal" gap={3}>
            <Stack style={{ justifyContent: "center" }}>
              <Card.Subtitle>You cannot delete the only {displayType} account.</Card.Subtitle>
            </Stack>
            <Button className="app-primary-color button" onClick={() => onClickCancelDelete()}>
              Cancel
            </Button>
          </Stack>
        </Card.Body>
      </Card>
    );
  };

  return (
    <Row>
      <Col xs={12}>
        <CaseReassignmentModal
          show={showCaseReassignmentModal}
          cases={casesToReassign}
          cmUsers={cmUsers}
          deletedUserId={userToDelete?.id}
          onComplete={handleCaseReassignmentComplete}
          setShowToast={setShowToast}
          setToastText={setToastText}
          setToastBody={setToastBody}
        />
        <Stack gap={3}>
          {/* with no users the heading is hidden; the "No Records" message carries the description instead */}
          {users.length > 0 && (
            <PageTitle
              title={
                type === "org_admin"
                  ? "Org Admin Users"
                  : type === "admin"
                    ? "Admin Users"
                    : type === "case_manager"
                      ? "Case Manager Users"
                      : type === "res_support"
                        ? "Residential Support Users"
                        : "Support Users"
              }
              type="section-sm"
              descriptionText={description}
            />
          )}
          {isLoading ? (
            <Container className="div-main">
              <Button size="sm" className="spinner-button spinner-button-main">
                <Spinner animation="border" style={{ color: "#F6893D" }} />
              </Button>
            </Container>
          ) : (
            <>
              {users.length === 0 && (
                <div className="mt-2">
                  <p className="mb-1">No {displayType} User Records</p>
                  {description && <p className="text-muted small mb-0">{description}</p>}
                </div>
              )}
              {users.map((user: any) =>
                isDeleting && userToDelete?.id === user.id ? (
                  // the confirmation replaces only the card of the user being deleted
                  <React.Fragment key={user.id}>
                    {user.id === auth?.profile?.id
                      ? renderCannotDeleteThemself()
                      : (totalCount ?? users?.length) === 1 && type === "org_admin"
                        ? renderCannotDeleteAll()
                        : renderDelete()}
                  </React.Fragment>
                ) : (
                  <Card key={user.id} className="bg-light">
                    <Card.Body className="py-2">
                      <Stack direction="horizontal" gap={3}>
                        <Image
                          className="image-people image-people-sm cursor-pointer"
                          src={user.profile?.[0]?.data ? "data:image/jpeg;base64, " + user.profile[0].data : "/blank-profile-picture.jpg"}
                          onClick={(e) => editUser(user.id)}
                        />
                        <Stack className="cursor-pointer" onClick={(e) => editUser(user.id)}>
                          <Card.Subtitle className="mt-1">
                            {user.firstname} {user.preferredname ? "'" + user.preferredname + "'" : ""} {user.lastname}
                          </Card.Subtitle>
                          <Card.Subtitle className="text-muted fw-normal small mt-1">{user.email} </Card.Subtitle>
                        </Stack>
                        {user.activeCases > 0 && (
                          <Card.Subtitle className="text-muted fw-normal small mt-1">
                            {`${user.activeCases} Active Case${user.activeCases > 1 ? "s" : ""}`}{" "}
                          </Card.Subtitle>
                        )}
                        {(user.waitlistCases > 0 || user.inactiveCases > 0) && (
                          <IconButton
                            icon={InfoCircleOutline}
                            tooltipText={getCaseTooltip(user)}
                            color="app-primary-color-inverse"
                            className="mt-1"
                          />
                        )}
                        <Button className="app-secondary-color button" onClick={(e) => editUser(user.id)}>
                          Edit
                        </Button>
                        <div className="vr" />
                        <Button className="app-danger-color button" onClick={(e) => onClickDelete(user)}>
                          Delete
                        </Button>
                      </Stack>
                    </Card.Body>
                  </Card>
                ),
              )}
            </>
          )}
        </Stack>
      </Col>
    </Row>
  );
};

export default UserTab;
